import React, { PureComponent } from 'react';
import { Query, VirtualBrand } from '~/Api/Analytics/VirtualBrand';
import Pagination from '~/Api/Pagination/Pagination';
import FilterParameterDescription from '~/libs/FilterParameter/FilterParameterDescription';
import AtcAutocomplete from '~/MedicinalBrandsAnalytics/AtcAutocomplete/AutocompleteContainer';
import BrandAutocomplete from '~/MedicinalBrandsAnalytics/BrandAutocomplete/AutocompleteContainer';
import TherapeuticAreaAutocomplete from '~/MedicinalBrandsAnalytics/TherapeuticAreaAutocomplete';
import HistoryPanel from '~/MedicinalBrandsAnalytics/VirtualBrand/HistoryPanel';

import DataSourceMap from '~/MedicinalBrandsAnalytics/DataSourceMap/';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Button from '~/UI/Button';
import FilterForm from '~/UI/FilterForm/FilterForm';
import Select from '~/UI/Select';
import SimplePaginator from '~/UI/SimplePaginator';
import List from '~/WindowedList';

import styles from './styles.less';
import { State as BrandDetailState, TabStateName } from './VirtualBrand/BrandDetail';
import BrandDetailContainer from './VirtualBrand/BrandDetailContainer';

import type { Props as BrandDetailParsedProps } from './VirtualBrand/BrandDetailUrlState';
import primaryIconButtonStyles from '~/UI/Button/primary-icon.less';
import Icon from '~/UI/Icon';

import { Status } from '~/Api/DataSourceStatus';
import { Result } from '~/Api/DataSourceStatus/findStatuses';

interface Props {
	availableParameters: FilterParameterDescription<string | string[]>[];
	currencies: string[];
	query: Query<string | string[]>;
	isFetching: boolean;
	parsedUrlDefaults: {
		[virtualBrandId: string]: BrandDetailParsedProps;
	};
	virtualBrands: VirtualBrand[] | null;
	pagination: Pagination | null;
	onSearch(query: Query): void;
	onLoad(query: Query): void;
	onUnload(): void;
	onExport(query: Query): void;
	onFetchDataSourceStatuses(): Promise<Result | null>;
}

interface State {
	virtualBrandsState: BrandDetailState[];
	showHistoryPanel: boolean | null;
	dataSourceStatuses: Status[];
	isStatusFetching: boolean;
}

export default class Page extends PureComponent<Props, State> {
	mediaQuery: MediaQueryList = window.matchMedia('(max-width: 1400px)');

	state: State = {
		virtualBrandsState: [],
		showHistoryPanel: false,
		dataSourceStatuses: [],
		isStatusFetching: true,
	};

	componentDidMount() {
		const { query, onLoad, onFetchDataSourceStatuses } = this.props;
		onLoad(query);
		this.mediaQuery.addListener(this.mediaQueryChanged);

		onFetchDataSourceStatuses().then((res) => {
			this.setState({ dataSourceStatuses: res ? res.items : [] });
			this.setState({ isStatusFetching: false });
		});
	}

	componentWillUnmount() {
		this.mediaQuery.removeListener(this.mediaQueryChanged);
		this.props.onUnload();
	}

	componentDidUpdate(prevProps: Props) {
		const { query, onLoad } = this.props;

		if (!prevProps.query.equals(query)) {
			this.setState({ virtualBrandsState: [] });
			onLoad(query);
		}
	}

	mediaQueryChanged = () => {
		if (this.state.showHistoryPanel === null) {
			this.forceUpdate();
		}
	};

	resolveShowHistoryPanel(): boolean {
		const { showHistoryPanel } = this.state;
		return showHistoryPanel !== null ? showHistoryPanel : !this.mediaQuery.matches;
	}

	render() {
		const { query, virtualBrands } = this.props;
		const showHistoryPanel = this.resolveShowHistoryPanel() && (Boolean(virtualBrands) || query.isValid);

		return (
			<div className={styles.container}>
				<div className={showHistoryPanel ? styles.paddedList : styles.list}>
					<List
						footer={this.renderFooter()}
						header={this.renderHeader(showHistoryPanel)}
						item={this.renderItem}
						itemsCount={virtualBrands?.length || 0}
					/>
				</div>

				{showHistoryPanel ? (
					<HistoryPanel query={query} onClose={() => this.setState({ showHistoryPanel: false })} />
				) : null}
			</div>
		);
	}

	renderHeader(showHistoryPanel: boolean) {
		const { availableParameters, currencies, query, pagination, onSearch, onExport } = this.props;

		return (
			<div className={styles.header}>
				<div className={styles.search}>
					<FilterForm
						autocompletes={{
							atc: (props: React.ComponentProps<typeof AtcAutocomplete>) => <AtcAutocomplete multiple {...props} />,
							brand: (props: React.ComponentProps<typeof BrandAutocomplete>) => (
								<BrandAutocomplete multiple {...props} />
							),
							'therapeutic-area': (props: React.ComponentProps<typeof TherapeuticAreaAutocomplete>) => (
								<TherapeuticAreaAutocomplete multiple {...props} />
							),
						}}
						availableParameters={availableParameters}
						errors={{}}
						filterParameters={query.filterParameters}
						onSearch={(filterParameters) => onSearch(query.filterParametersUpdated(filterParameters))}
					/>

					<div className={styles.searchText}>
						<h1 className={styles.heading}>{pagination ? `Search results (${pagination.total})` : null}</h1>

						{pagination ? (
							<div className={styles.headerActions}>
								<span style={{ marginRight: 8 }}>
									<Button styles={primaryIconButtonStyles} onClick={() => onExport(query)}>
										<Icon name='download'>download</Icon> Download
									</Button>
								</span>
								<Button
									styles={{ button: styles.historyButton }}
									onClick={() => this.setState({ showHistoryPanel: !showHistoryPanel })}
								>
									Latest News
								</Button>

								<Select
									value={query.currency || 'source'}
									onChange={(event) => onSearch(query.currencyUpdated(event.target.value))}
								>
									<option value='source'>Source currency</option>
									{currencies.map((currency) => (
										<option key={currency} value={currency}>
											{currency}
										</option>
									))}
								</Select>
							</div>
						) : null}
					</div>
				</div>
			</div>
		);
	}

	renderItem = (index: number) => {
		const {
			props: { virtualBrands, parsedUrlDefaults, query },
			state: { virtualBrandsState },
		} = this;
		if (!virtualBrands) {
			return null;
		}

		const virtualBrand = virtualBrands[index];
		const virtualBranDefaults = parsedUrlDefaults[virtualBrand.id] || null;

		const defaultTabState = virtualBranDefaults
			? {
					...(virtualBranDefaults?.tabName
						? { tab: { name: virtualBranDefaults.tabName }, packId: virtualBranDefaults.virtualPackId || null }
						: {}),
			  }
			: undefined;

		return (
			<BrandDetailContainer
				currency={query.currency}
				state={virtualBrandsState[index] || defaultTabState}
				virtualBrand={virtualBrand}
				onStateChange={(state: BrandDetailState) => {
					const newVirtualBrandsState: BrandDetailState[] = [...virtualBrandsState];
					newVirtualBrandsState[index] = state;
					this.setState(() => ({
						virtualBrandsState: newVirtualBrandsState,
						showHistoryPanel: state.tab.name === TabStateName.info,
					}));
				}}
			/>
		);
	};

	renderFooter() {
		const { query, isFetching, pagination, onLoad } = this.props;

		if (isFetching) {
			return <ActivityIndicator />;
		}
		if (this.state.isStatusFetching) {
			return <ActivityIndicator />;
		}
		if (!pagination) {
			return <DataSourceMap datasource={this.state.dataSourceStatuses} />;
		}

		if (pagination.hasNextPage()) {
			return <SimplePaginator count={pagination.perPage} onLoadMore={() => onLoad(query)} />;
		}

		return null;
	}
}
